import { z } from "zod";

import { MAX_LENGTH_MESSAGE, MIN_LENGTH_MESSAGE, REQUIRED_MESSAGE } from "@/utils";

import { EProductType } from "../products.type";
import {
  mapWarrantyMarkupValueFromApi,
  normalizeLegacyWarrantyCommercialization,
  warrantyProductConfigSchema,
} from "./warranty-insurance.schema";

const centsToDecimal = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  return Number(value) / 100;
};

const stripHtml = (v: string | undefined | null) =>
  (v ?? "").replace(/<[^>]*>/g, " ").trim();

export const theftInsuranceSchema = z
  .object({
    type: z.literal(EProductType.THEFT_INSURANCE),
    name: z
      .string()
      .min(2, { message: MIN_LENGTH_MESSAGE("2") })
      .max(150, { message: MAX_LENGTH_MESSAGE("150") }),
    insurance_company_id: z.string().uuid({ message: REQUIRED_MESSAGE }),
    policy_number: z.string().optional().nullable(),
    susep_code: z.string().optional().nullable(),
    iof_value: z
      .preprocess((v) => (v === "" || v === null ? 0 : v), z.coerce.number())
      .default(0),
    type_of_vigency: z.enum(
      [
        "pluriannual",
        "annual",
        "monthly",
        "daily",
        "pay_per_use",
        "lifetime",
      ],
      { message: REQUIRED_MESSAGE },
    ),
    policy_start_date: z.coerce.date().optional(),
    policy_end_date: z.coerce.date().optional(),
    min_permitted_age: z.coerce.number().optional().nullable(),
    max_permitted_age: z.coerce.number().optional().nullable(),
    description: z.string().optional(),
    insurance_coverages: z.string().optional(),
    general_conditions: z.string().optional(),
    excluded_risks: z.string().optional(),
    infinity_sync_as_source: z.boolean().optional(),
    external_product_id: z
      .string()
      .max(255, { message: MAX_LENGTH_MESSAGE("255") })
      .optional()
      .nullable(),
    theft_product_config: warrantyProductConfigSchema,
  })
  .superRefine((data, ctx) => {
    if (!stripHtml(data.description)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["description"],
      });
    }
    if (!stripHtml(data.insurance_coverages)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["insurance_coverages"],
      });
    }
    if (!stripHtml(data.general_conditions)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["general_conditions"],
      });
    }
    if (!stripHtml(data.excluded_risks)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["excluded_risks"],
      });
    }
    if (data.theft_product_config.commercialization_rules.includes("age")) {
      if (
        data.min_permitted_age === undefined ||
        data.min_permitted_age === null ||
        data.max_permitted_age === undefined ||
        data.max_permitted_age === null
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["min_permitted_age"],
        });
      }
    }
    const w = data.theft_product_config;
    if (w.emission_type === "apolice" && w.is_collective_policy === true) {
      if (!data.policy_number?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["policy_number"],
        });
      }
      if (!data.policy_start_date || !data.policy_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["policy_start_date"],
        });
      }
    }
    if (w.has_additional_plans) {
      if (!w.plans?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["theft_product_config", "plans"],
        });
      }
    }
  });

export type TTheftInsuranceFormData = z.infer<typeof theftInsuranceSchema>;

const defaultTheftConfig = (): TTheftInsuranceFormData["theft_product_config"] => ({
  emission_type: "bilhete",
  is_collective_policy: false,
  contract_active: false,
  commercialization_rules: ["age"],
  premium_price_type: "net",
  net_price: 0,
  markup_enabled: false,
  pro_labore_enabled: false,
  has_additional_plans: false,
  plans: [],
});

export function getDefaultTheftProductForm(): TTheftInsuranceFormData {
  return {
    type: EProductType.THEFT_INSURANCE,
    name: "",
    insurance_company_id: "",
    policy_number: "",
    susep_code: null,
    iof_value: 0,
    type_of_vigency: "annual",
    policy_start_date: undefined,
    policy_end_date: undefined,
    min_permitted_age: 0,
    max_permitted_age: 120,
    description: "",
    insurance_coverages: "",
    general_conditions: "",
    excluded_risks: "",
    infinity_sync_as_source: true,
    external_product_id: "",
    theft_product_config: defaultTheftConfig(),
  };
}

export function mergeTheftProductConfigFromApi(
  raw: unknown,
): TTheftInsuranceFormData["theft_product_config"] {
  const base = defaultTheftConfig();
  if (!raw || typeof raw !== "object") {
    return base;
  }
  const c = raw as Record<string, unknown>;
  const toDate = (v: unknown) =>
    v === null || v === undefined || v === ""
      ? undefined
      : new Date(String(v));

  const plans = Array.isArray(c.plans)
    ? (c.plans as Array<Record<string, unknown>>).map((p) => ({
        ...p,
        adjustments: Array.isArray(p.adjustments)
          ? (p.adjustments as Array<Record<string, unknown>>).map((a) => ({
              ...a,
              new_assistance_value: centsToDecimal(a.new_assistance_value),
            }))
          : [],
      }))
    : base.plans;

  const rawRules = Array.isArray(c.commercialization_rules)
    ? [...(c.commercialization_rules as string[])]
    : [...base.commercialization_rules];
  const norm = normalizeLegacyWarrantyCommercialization(
    rawRules,
    c.vehicle_age_rule,
  );

  const markupValueFromApi = mapWarrantyMarkupValueFromApi(c, base.markup_value);

  return {
    ...base,
    ...c,
    emission_type: (c.emission_type as typeof base.emission_type) ?? base.emission_type,
    commercialization_rules:
      norm.commercialization_rules as typeof base.commercialization_rules,
    vehicle_age_rule: norm.vehicle_age_rule as typeof base.vehicle_age_rule,
    contract_start_date: toDate(c.contract_start_date),
    contract_end_date: toDate(c.contract_end_date),
    net_price: centsToDecimal(c.net_price) ?? base.net_price,
    commissioned_price:
      centsToDecimal(c.commissioned_price) ?? base.commissioned_price,
    pro_labore_value: centsToDecimal(c.pro_labore_value) ?? base.pro_labore_value,
    markup_enabled: Boolean(c.markup_enabled ?? base.markup_enabled),
    markup_mode: (c.markup_mode as typeof base.markup_mode) ?? base.markup_mode,
    markup_value: markupValueFromApi,
    plans: plans as TTheftInsuranceFormData["theft_product_config"]["plans"],
  };
}
